import { materialTypeFormat } from '@/common/material-type-formatting';
import { Button, Card, Flex, Grid, Text, Title } from '@mantine/core';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState } from 'react';
import { MaterialType } from '../../../../../../../backend/src/types.generated';
import { useBagScanContext } from '../hook';
import styles from '../styles.module.css';

export const BagScanEvaluateBagPage = () => {
	const { evaluatingBag, handleBagAction } = useBagScanContext();
	const [confirmedMaterial, setConfirmedMaterial] = useState<MaterialType | null | undefined>(
		evaluatingBag?.shouldConfirmMaterial ? null : evaluatingBag?.intendedMaterial
	);
	const [evalWorkflow, setEvalWorkflow] = useState<
		'IDENTIFY_MATERIAL' | 'VERIFY_MATERIAL' | 'EVALUATE_BAG' | null
	>(null);

	useEffect(() => {
		if (evaluatingBag?.shouldConfirmMaterial && evaluatingBag?.intendedMaterial) {
			setEvalWorkflow('VERIFY_MATERIAL');
		} else if (!confirmedMaterial || !evaluatingBag?.intendedMaterial) {
			setEvalWorkflow('IDENTIFY_MATERIAL');
		} else {
			setEvalWorkflow('EVALUATE_BAG');
		}
	}, [evaluatingBag?.shouldConfirmMaterial, confirmedMaterial, evaluatingBag?.intendedMaterial]);

	// Glass material types to exclude from the selection
	const excludedGlassMaterialTypes = new Set([
		MaterialType.GlassCoarse,
		MaterialType.GlassFine,
		MaterialType.GlassMedium,
		MaterialType.GlassMoondust,
		MaterialType.GlassSf_300_150,
		MaterialType.GlassSf_500_300,
		MaterialType.GlassSuperfine,
	]);

	const materialTypes = Object.values(MaterialType)
		.filter((m) => !excludedGlassMaterialTypes.has(m))
		.map((materialType) => {
			const config = materialTypeFormat[materialType];
			return {
				name: config.label,
				value: materialType,
				emoji: config.emoji,
			};
		})
		.reverse();

	return (
		<Flex pos="absolute" h="100vh" w="100%" bg="var(--mantine-color-green-0)">
			<Flex pos="fixed" top={0} left={0} p="md" bg="white" w="100%" align="center" gap="sm">
				<Title order={2}>Evaluation</Title>
			</Flex>

			<AnimatePresence mode="wait">
				{evalWorkflow === 'EVALUATE_BAG' && (
					<motion.div
						initial={{ opacity: 0, scale: 0.95, y: 20 }}
						animate={{ opacity: 1, scale: 1, y: 0 }}
						exit={{ opacity: 0, scale: 0.95, y: -20 }}
						transition={{
							duration: 0.4,
							ease: [0.4, 0.0, 0.2, 1],
						}}
						className={styles.evaluateContainer}
						key="evaluate-bag"
					>
						<Flex px="lg" w="100%" direction="column" justify="center" gap="lg">
							<motion.div
								initial={{ opacity: 0, y: 20, scale: 0.95 }}
								animate={{ opacity: 1, y: 0, scale: 1 }}
								transition={{ duration: 0.6, delay: 0.1, ease: [0.4, 0.0, 0.2, 1] }}
							>
								<Card shadow="md" p="lg" radius="md" bg="white">
									<Flex direction="column" align="center" gap="lg">
										{evaluatingBag?.shouldConfirmMaterial || !evaluatingBag?.intendedMaterial ? (
											<>
												{/* User already identified/confirmed material - simple display */}
												<Text size="lg" ta="center" c="dark" fw={600}>
													What do you want to do with this
												</Text>
												<Flex align="center" gap="xs">
													<Text size="lg">
														{confirmedMaterial
															? materialTypeFormat[confirmedMaterial]?.emoji
															: '❓'}
													</Text>
													<Text size="lg" fw={600}>
														{confirmedMaterial
															? materialTypeFormat[confirmedMaterial]?.label
															: 'unknown'}
													</Text>
													<Text size="lg">material?</Text>
												</Flex>
											</>
										) : (
											<>
												{/* User went straight to evaluation - show big material display */}
												<Text size="lg" ta="center" c="dark" fw={600}>
													This bag contains
												</Text>

												{confirmedMaterial && <MaterialTypeCell materialType={confirmedMaterial} />}

												<Text size="md" ta="center" fw={500}>
													What do you want to do with this bag?
												</Text>
											</>
										)}
									</Flex>
								</Card>
							</motion.div>
							<motion.div
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{
									duration: 0.4,
									ease: [0.4, 0.0, 0.2, 1],
									delay: 0.3,
								}}
							>
								<Flex gap="sm">
									<Button
										fullWidth
										bg="red"
										c="white"
										variant="filled"
										onClick={() =>
											confirmedMaterial && handleBagAction('REJECT', confirmedMaterial)
										}
									>
										Reject
									</Button>
									<Button
										fullWidth
										bg="orange"
										c="white"
										variant="filled"
										onClick={() => confirmedMaterial && handleBagAction('AUDIT', confirmedMaterial)}
									>
										Audit
									</Button>
									<Button
										fullWidth
										bg="green"
										c="white"
										variant="filled"
										onClick={() =>
											confirmedMaterial && handleBagAction('ACCEPT', confirmedMaterial)
										}
									>
										Accept
									</Button>
								</Flex>
							</motion.div>
						</Flex>
					</motion.div>
				)}
				{evalWorkflow === 'IDENTIFY_MATERIAL' && (
					<motion.div
						initial={{ opacity: 0, scale: 0.95, y: 20 }}
						animate={{ opacity: 1, scale: 1, y: 0 }}
						exit={{ opacity: 0, scale: 0.95, y: -20 }}
						transition={{
							duration: 0.4,
							ease: [0.4, 0.0, 0.2, 1],
						}}
						className={styles.evaluateContainer}
						key="identify-material"
					>
						<Flex px="lg" w="100%" direction="column" justify="center" gap="lg">
							<motion.div
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.5, delay: 0.1, ease: [0.4, 0.0, 0.2, 1] }}
							>
								<Flex direction="column">
									<Title order={2} ta="center">
										What material is in this bag?
									</Title>
									<Text size="sm" ta="center" c="dimmed" mt="xs">
										Select the material type you see
									</Text>
								</Flex>
							</motion.div>
							<Grid gutter="sm">
								{materialTypes.map((materialType, index) => (
									<Grid.Col key={materialType.value} span={6}>
										<motion.div
											initial={{ opacity: 0, y: 30, scale: 0.8 }}
											animate={{ opacity: 1, y: 0, scale: 1 }}
											transition={{
												duration: 0.5,
												delay: 0.25 + index * 0.08,
												ease: [0.4, 0.0, 0.2, 1],
											}}
										>
											<Card
												shadow="sm"
												padding="sm"
												radius="md"
												withBorder
												className={styles.materialTypeCard}
												onClick={() => {
													setConfirmedMaterial(materialType.value);
													setEvalWorkflow('EVALUATE_BAG');
												}}
											>
												<Flex direction="column" align="center" gap="xs">
													<Text size="1.5rem" className={styles.materialEmojiText}>
														{materialType.emoji}
													</Text>
													<Text size="xs" ta="center" fw={500}>
														{materialType.name}
													</Text>
												</Flex>
											</Card>
										</motion.div>
									</Grid.Col>
								))}
							</Grid>
						</Flex>
					</motion.div>
				)}
				{evalWorkflow === 'VERIFY_MATERIAL' && evaluatingBag?.intendedMaterial && (
					<motion.div
						initial={{ opacity: 0, scale: 0.95, y: 20 }}
						animate={{ opacity: 1, scale: 1, y: 0 }}
						exit={{ opacity: 0, scale: 0.95, y: -20 }}
						transition={{
							duration: 0.4,
							ease: [0.4, 0.0, 0.2, 1],
						}}
						className={styles.evaluateContainer}
						key="verify-material"
					>
						<Flex px="lg" w="100%" direction="column" justify="center" gap="lg">
							<motion.div
								initial={{ opacity: 0, y: 20, scale: 0.95 }}
								animate={{ opacity: 1, y: 0, scale: 1 }}
								transition={{ duration: 0.6, delay: 0.1, ease: [0.4, 0.0, 0.2, 1] }}
							>
								<Card shadow="md" p="lg" radius="md" bg="white">
									<Flex direction="column" align="center" gap="lg">
										<Text size="lg" ta="center" c="dark" fw={600}>
											Please verify this bag contains
										</Text>

										<MaterialTypeCell materialType={evaluatingBag?.intendedMaterial} />

										<Text size="sm" ta="center" fw={500}>
											Open the bag and check the contents match
										</Text>
									</Flex>
								</Card>
							</motion.div>
							<motion.div
								initial={{ opacity: 0, y: 30 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.5, delay: 0.4, ease: [0.4, 0.0, 0.2, 1] }}
							>
								<Flex direction="row" pt="lg" gap="md" justify="center">
									<Button
										fullWidth
										c="white"
										bg="red"
										onClick={() => setEvalWorkflow('IDENTIFY_MATERIAL')}
									>
										Different Material
									</Button>
									<Button
										fullWidth
										c="white"
										bg="green"
										onClick={() => {
											setConfirmedMaterial(evaluatingBag?.intendedMaterial);
											setEvalWorkflow('EVALUATE_BAG');
										}}
									>
										Confirmed
									</Button>
								</Flex>
							</motion.div>
						</Flex>
					</motion.div>
				)}
			</AnimatePresence>
		</Flex>
	);
};

// Material Type Cell Component with emoji
const MaterialTypeCell = ({ materialType }: { materialType: MaterialType }) => {
	const config = materialTypeFormat[materialType];

	return (
		<Flex align="center" direction="column" gap="sm">
			<motion.span
				initial={{ opacity: 0, scale: 0.3, y: 20 }}
				animate={{ opacity: 1, scale: 1, y: 0 }}
				transition={{
					duration: 0.6,
					ease: [0.4, 0.0, 0.2, 1],
					type: 'spring',
					stiffness: 120,
					damping: 12,
				}}
				className={styles.materialTypeCellEmoji}
			>
				<span>{config?.emoji ?? '🤔'}</span>
			</motion.span>

			<motion.div
				initial={{ opacity: 0, y: 30 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{
					delay: 0.2,
					duration: 0.5,
					ease: [0.4, 0.0, 0.2, 1],
				}}
			>
				<Title order={1}>{config?.label ?? 'Unknown'}</Title>
			</motion.div>
		</Flex>
	);
};
